/**
 * Pure scoring math for the Custom Weighted Scoring Builder.
 *
 * This module is intentionally framework-free: no React, no IO, no fetch.
 * Everything is deterministic functions on plain data so the math is testable
 * in isolation and the same code runs in the live UI and in unit tests.
 *
 * High-level shape of one ScoringResult (the value applyPresetToTickerData
 * returns per row ticker):
 *
 *   {
 *     ticker: 'AAPL',
 *     aggregateScore: 7.4 | null,          // null when every cell is null
 *     variance: 0.82,                       // variance of cell scores; 0 if <2 valid
 *     cells: {
 *       col_1: { raw, transformed, score, bucketHit, contribution, error? },
 *       ...
 *     }
 *   }
 *
 * `tickerData` is the merged research bundle, keyed by ticker:
 *
 *   {
 *     fundamentalsByTicker: { AAPL: {<row>}, MSFT: {<row>}, ... },
 *     financialsByTicker: { AAPL: { revenue: {2020:..., TTM:...}, ... }, ... },
 *     epsTrendsByTicker:  { AAPL: {<row>}, ... },
 *     years: [2019, ..., 'TTM']             // ordering of financials columns
 *   }
 */

// ---------------------------------------------------------------------------
// Field resolution
// ---------------------------------------------------------------------------

/**
 * Read the value of a single catalog field for one ticker out of the merged
 * research bundle. Returns null when the field or ticker is missing.
 */
export function getFieldValue(fieldKey, tickerData, ticker, catalog) {
  if (!ticker || !tickerData || !catalog) return null;
  const def = catalog.byKey ? catalog.byKey[fieldKey] : catalog[fieldKey];
  if (!def) return null;

  const { section, responseField } = def;
  if (section === 'fundamentals') {
    const row = tickerData.fundamentalsByTicker?.[ticker];
    return row ? coerceNumber(row[responseField]) : null;
  }
  if (section === 'financials_latest') {
    const tickerRatios = tickerData.financialsByTicker?.[ticker];
    if (!tickerRatios) return null;
    const series = tickerRatios[responseField];
    if (!series) return null;
    return coerceNumber(latestPeriodValue(series, tickerData.years));
  }
  // Raw EPS-trend snapshot fields. Both the legacy 'eps_trends' tag (used by
  // earlier tests + any saved presets from before the catalog refactor) and
  // the renamed 'eps_trends_raw_advanced' section route here.
  if (section === 'eps_trends' || section === 'eps_trends_raw_advanced') {
    const row = tickerData.epsTrendsByTicker?.[ticker];
    return row ? coerceNumber(row[responseField]) : null;
  }
  // Derived EPS metrics (revisions & growth). These are pure transformations
  // of the raw timepoints already in epsTrendsByTicker — computed on-the-fly
  // here so we don't need to add them to the API payload.
  // The catalog entry carries `op` + `computedFrom: [latestField, priorField]`.
  if (section === 'eps_trends_derived') {
    const row = tickerData.epsTrendsByTicker?.[ticker];
    if (!row) return null;
    const args = Array.isArray(def.computedFrom) ? def.computedFrom : null;
    if (!args || args.length < 2) return null;
    const a = coerceNumber(row[args[0]]);
    const b = coerceNumber(row[args[1]]);
    if (a == null || b == null) return null;
    if (def.op === 'pct_change') {
      return b === 0 ? null : (a - b) / Math.abs(b);
    }
    // No other ops needed for PR1; extend here as new derived ops appear.
    return null;
  }
  return null;
}

/**
 * Pull the latest period value out of a financials series.
 * Prefers 'TTM' if present; else falls back to the most recent numeric year
 * present in `years`.
 */
function latestPeriodValue(series, years) {
  if (series == null) return null;
  if (Object.prototype.hasOwnProperty.call(series, 'TTM') && series.TTM != null) {
    return series.TTM;
  }
  // numeric years descending
  const numericYears = (years || Object.keys(series))
    .filter((y) => typeof y === 'number' || /^\d+$/.test(String(y)))
    .map((y) => Number(y))
    .sort((a, b) => b - a);
  for (const y of numericYears) {
    if (series[y] != null) return series[y];
    if (series[String(y)] != null) return series[String(y)];
  }
  return null;
}

/**
 * Used by yoy(field) to read the period immediately prior to the latest one
 * `latestPeriodValue` would return. If TTM is present and non-null then TTM
 * is "latest" and the prior is the most recent numeric year. Otherwise the
 * prior is the second-most-recent numeric year.
 */
function priorPeriodValue(series, years) {
  if (series == null) return null;
  const numericYears = (years || Object.keys(series))
    .filter((y) => typeof y === 'number' || /^\d+$/.test(String(y)))
    .map((y) => Number(y))
    .sort((a, b) => b - a);
  const latestIsTTM =
    Object.prototype.hasOwnProperty.call(series, 'TTM') && series.TTM != null;
  const priorYearIdx = latestIsTTM ? 0 : 1;
  const priorYear = numericYears[priorYearIdx];
  if (priorYear == null) return null;
  return series[priorYear] != null ? series[priorYear] : series[String(priorYear)];
}

/**
 * Coerce strings, booleans, etc. into a finite number or null.
 * NaN, Infinity, undefined, '' all become null.
 */
function coerceNumber(v) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

// ---------------------------------------------------------------------------
// Formula evaluation (derived columns)
// ---------------------------------------------------------------------------

const BIN_OPS = {
  add: (a, b) => a + b,
  sub: (a, b) => a - b,
  mul: (a, b) => a * b,
  div: (a, b) => (b === 0 ? null : a / b),
  pct_change: (a, b) => (b === 0 || b == null ? null : (a - b) / Math.abs(b)),
  max: (a, b) => Math.max(a, b),
  min: (a, b) => Math.min(a, b),
  gt: (a, b) => (a > b ? 1 : 0),
  lt: (a, b) => (a < b ? 1 : 0),
};
const UNARY_OPS = {
  abs: (a) => Math.abs(a),
  neg: (a) => -a,
};

/**
 * Resolve a single formula argument:
 *   - number literal -> as-is
 *   - 'col_*' string -> recurse into another column's evaluated value
 *   - other string   -> catalog field key, resolve via getFieldValue
 *   - object         -> nested formula, recurse
 */
function resolveArg(arg, ctx) {
  if (typeof arg === 'number') return arg;
  if (typeof arg === 'string') {
    if (arg.startsWith('col_')) {
      const ref = ctx.columnsById[arg];
      if (!ref) return null;
      // Evaluate-on-demand with memoization to avoid recomputing.
      if (ctx.evaluated.has(arg)) return ctx.evaluated.get(arg);
      const val = evaluateColumnRaw(ref, ctx);
      ctx.evaluated.set(arg, val);
      return val;
    }
    return getFieldValue(arg, ctx.tickerData, ctx.ticker, ctx.catalog);
  }
  if (arg && typeof arg === 'object') return evaluateFormula(arg, ctx);
  return null;
}

/**
 * Evaluate a formula tree. Returns a number or null.
 */
export function evaluateFormula(formula, ctx) {
  if (!formula || typeof formula !== 'object') return null;
  const { op, args } = formula;
  if (!Array.isArray(args)) return null;

  if (op === 'yoy') {
    // yoy(fieldKey) => (latest - prior) / |prior| over financials series
    const fieldKey = args[0];
    if (typeof fieldKey !== 'string') return null;
    const def = ctx.catalog.byKey?.[fieldKey];
    if (!def || def.section !== 'financials_latest') return null;
    const series = ctx.tickerData.financialsByTicker?.[ctx.ticker]?.[def.responseField];
    const latest = latestPeriodValue(series, ctx.tickerData.years);
    const prior = priorPeriodValue(series, ctx.tickerData.years);
    if (latest == null || prior == null || prior === 0) return null;
    return (Number(latest) - Number(prior)) / Math.abs(Number(prior));
  }

  if (op in UNARY_OPS) {
    const a = resolveArg(args[0], ctx);
    if (a == null) return null;
    const out = UNARY_OPS[op](a);
    return Number.isFinite(out) ? out : null;
  }

  if (op in BIN_OPS) {
    const a = resolveArg(args[0], ctx);
    const b = resolveArg(args[1], ctx);
    if (a == null || b == null) return null;
    const out = BIN_OPS[op](a, b);
    if (out == null) return null;
    return Number.isFinite(out) ? out : null;
  }

  return null;
}

/**
 * Compute the *raw* (pre-rubric) value of one column for one ticker.
 *
 * For raw columns: read the catalog field, swapping in benchmarkTicker if set.
 * For derived columns: evaluate the formula in the same ticker context (or
 * benchmark context if benchmarkTicker is set).
 */
function evaluateColumnRaw(column, ctxAtRow) {
  const tickerForThisCol = column.benchmarkTicker || ctxAtRow.ticker;
  const ctx = { ...ctxAtRow, ticker: tickerForThisCol };

  if (column.kind === 'raw') {
    return getFieldValue(column.fieldKey, ctx.tickerData, tickerForThisCol, ctx.catalog);
  }
  if (column.kind === 'derived') {
    return evaluateFormula(column.formula, ctx);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Rubric application
// ---------------------------------------------------------------------------

const SCORE_MIN = 1;
const SCORE_MAX = 10;
const NEUTRAL_SCORE = 5.5;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Apply transform stage. PR1 only supports 'identity'. */
export function applyTransform(value, transform) {
  if (value == null) return null;
  if (!transform || transform.type === 'identity') return value;
  return null; // unknown transform: signal "couldn't compute"
}

/**
 * Map a transformed value to a 1..10 score given the rubric.map shape and
 * direction. Returns null when value is null or rubric is malformed.
 *
 * `quantileContext` is required for type='quantile'. Shape:
 *   { sortedValues: number[]  // ascending, non-null only for this column
 *     allNullOrSingleton: boolean }
 */
export function applyMap(value, map, direction, quantileContext) {
  if (value == null || !map) return { score: null };
  const params = map.params || {};
  const dir = direction === 'lower' ? 'lower' : 'higher';

  if (map.type === 'linear') {
    const { min, max } = params;
    if (typeof min !== 'number' || typeof max !== 'number' || min === max) {
      return { score: null };
    }
    const range = max - min;
    const norm = (value - min) / range;             // 0..1 for higher-is-better
    const adjusted = dir === 'lower' ? 1 - norm : norm;
    const score = SCORE_MIN + (SCORE_MAX - SCORE_MIN) * adjusted;
    return { score: clamp(score, SCORE_MIN, SCORE_MAX) };
  }

  if (map.type === 'threshold') {
    const buckets = Array.isArray(params.buckets) ? params.buckets : [];
    if (!buckets.length) return { score: null };
    // Buckets are evaluated in order. The first one with `max == null` or
    // `value <= max` wins. For direction='lower' we flip to `value >= min`-style
    // semantics by reversing the comparison.
    if (dir === 'higher') {
      for (let i = 0; i < buckets.length; i += 1) {
        const b = buckets[i];
        const bMax = b.max;
        if (bMax == null || value <= bMax) {
          return { score: clamp(b.score, SCORE_MIN, SCORE_MAX), bucketHit: i };
        }
      }
    } else {
      // direction='lower' — first bucket with `min == null` or value >= min wins
      for (let i = 0; i < buckets.length; i += 1) {
        const b = buckets[i];
        const bMin = b.min;
        if (bMin == null || value >= bMin) {
          return { score: clamp(b.score, SCORE_MIN, SCORE_MAX), bucketHit: i };
        }
      }
    }
    // Fallback to the last bucket
    const last = buckets[buckets.length - 1];
    return { score: clamp(last.score, SCORE_MIN, SCORE_MAX), bucketHit: buckets.length - 1 };
  }

  if (map.type === 'boolean') {
    const { condition, threshold, trueScore, falseScore } = params;
    let pass = false;
    switch (condition) {
      case '>': pass = value > threshold; break;
      case '<': pass = value < threshold; break;
      case '>=': pass = value >= threshold; break;
      case '<=': pass = value <= threshold; break;
      case '==': pass = value === threshold; break;
      default: return { score: null };
    }
    return { score: clamp(pass ? trueScore : falseScore, SCORE_MIN, SCORE_MAX) };
  }

  if (map.type === 'quantile') {
    if (!quantileContext) return { score: null };
    const { sortedValues, allNullOrSingleton } = quantileContext;
    if (allNullOrSingleton) return { score: NEUTRAL_SCORE, degraded: true };
    const N = sortedValues.length;
    // Average rank for ties: index of first >= value, plus index of last <= value, /2.
    let lo = 0;
    while (lo < N && sortedValues[lo] < value) lo += 1;
    let hi = lo;
    while (hi < N && sortedValues[hi] === value) hi += 1;
    const rank = (lo + hi - 1) / 2;            // 0-indexed average
    const quantile = N === 1 ? 0.5 : rank / (N - 1);
    const adjusted = dir === 'lower' ? 1 - quantile : quantile;
    const score = SCORE_MIN + (SCORE_MAX - SCORE_MIN) * adjusted;
    return { score: clamp(score, SCORE_MIN, SCORE_MAX) };
  }

  return { score: null };
}

// ---------------------------------------------------------------------------
// Cell + aggregate scoring
// ---------------------------------------------------------------------------

/**
 * Score one cell (one column for one ticker). Returns provenance the UI uses
 * for hover drill-down and CSV export.
 */
export function scoreCell(rawValue, rubric, quantileContext) {
  if (!rubric) return { raw: rawValue, transformed: null, score: null };
  const transformed = applyTransform(rawValue, rubric.transform);
  const mapResult = applyMap(transformed, rubric.map, rubric.direction, quantileContext);
  return {
    raw: rawValue,
    transformed,
    score: mapResult.score,
    bucketHit: mapResult.bucketHit ?? null,
    degraded: mapResult.degraded ?? false,
  };
}

/**
 * Weighted-mean aggregation. Null cells are excluded; weights auto-normalize
 * over the surviving cells. Returns null aggregate when zero cells survive or
 * the surviving total weight is zero.
 *
 * Returns { score, contributions, variance } where:
 *   - score = weighted mean of valid cell scores
 *   - contributions = { col_id: weight*score / totalWeight } (sums to score)
 *   - variance = sample variance of valid cell scores (0 when <2)
 */
export function aggregateScore(cellResults, columns) {
  let totalWeight = 0;
  const valid = [];
  for (const col of columns) {
    const cell = cellResults[col.id];
    if (cell && cell.score != null && col.weight > 0) {
      totalWeight += col.weight;
      valid.push({ col, cell });
    }
  }
  if (!valid.length || totalWeight === 0) {
    return { score: null, contributions: {}, variance: 0 };
  }

  const contributions = {};
  let weightedSum = 0;
  for (const { col, cell } of valid) {
    const contribution = (col.weight * cell.score) / totalWeight;
    contributions[col.id] = contribution;
    weightedSum += contribution;
  }
  const score = weightedSum;

  // Sample variance of the (unweighted) valid cell scores
  let variance = 0;
  if (valid.length >= 2) {
    const mean = valid.reduce((s, v) => s + v.cell.score, 0) / valid.length;
    variance = valid.reduce((s, v) => s + (v.cell.score - mean) ** 2, 0) / valid.length;
  }

  return { score, contributions, variance };
}

// ---------------------------------------------------------------------------
// Quantile pre-pass
// ---------------------------------------------------------------------------

/**
 * Build a per-column quantileContext map needed before scoring rows.
 * Universe = the set of row tickers (current_list) — benchmark tickers do not
 * participate in quantile rank. The values used are the post-transform values
 * for each row ticker, *not* the raw values, so log/winsorize land naturally.
 */
function buildQuantileContexts(preset, rowTickers, tickerData, catalog) {
  const contexts = {};
  for (const col of preset.columns) {
    if (col.rubric?.map?.type !== 'quantile') continue;
    const values = [];
    for (const ticker of rowTickers) {
      const ctx = {
        ticker,
        tickerData,
        catalog,
        columnsById: indexById(preset.columns),
        evaluated: new Map(),
      };
      const raw = evaluateColumnRaw(col, ctx);
      const transformed = applyTransform(raw, col.rubric.transform);
      if (transformed != null) values.push(transformed);
    }
    values.sort((a, b) => a - b);
    contexts[col.id] = {
      sortedValues: values,
      allNullOrSingleton: values.length < 2,
    };
  }
  return contexts;
}

function indexById(columns) {
  const out = {};
  for (const c of columns) out[c.id] = c;
  return out;
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

/**
 * Score every row ticker against the preset.
 * Returns a sorted array of ScoringResult (default: aggregate desc, tiebreak
 * by variance asc, then ticker alpha).
 */
export function applyPresetToTickerData(preset, rowTickers, tickerData, catalog) {
  if (!preset || !Array.isArray(preset.columns) || !rowTickers?.length) return [];

  const columnsById = indexById(preset.columns);
  const quantileContexts = buildQuantileContexts(preset, rowTickers, tickerData, catalog);
  const results = [];

  for (const ticker of rowTickers) {
    const ctx = {
      ticker,
      tickerData,
      catalog,
      columnsById,
      evaluated: new Map(),
    };

    const cells = {};
    for (const col of preset.columns) {
      const raw = evaluateColumnRaw(col, ctx);
      const transformed = applyTransform(raw, col.rubric?.transform);
      const mapResult = applyMap(
        transformed,
        col.rubric?.map,
        col.rubric?.direction,
        quantileContexts[col.id],
      );
      cells[col.id] = {
        raw,
        transformed,
        score: mapResult.score,
        bucketHit: mapResult.bucketHit ?? null,
        degraded: mapResult.degraded ?? false,
      };
    }

    const agg = aggregateScore(cells, preset.columns);
    // Stamp contributions back onto each cell for hover drill-down.
    for (const id of Object.keys(cells)) {
      cells[id].contribution = agg.contributions[id] ?? null;
    }
    results.push({
      ticker,
      aggregateScore: agg.score,
      variance: agg.variance,
      cells,
    });
  }

  // Default sort: aggregate desc, tiebreak variance asc, then ticker alpha.
  results.sort((a, b) => {
    const aHas = a.aggregateScore != null;
    const bHas = b.aggregateScore != null;
    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;
    if (aHas && bHas && a.aggregateScore !== b.aggregateScore) {
      return b.aggregateScore - a.aggregateScore;
    }
    if (a.variance !== b.variance) return a.variance - b.variance;
    return a.ticker.localeCompare(b.ticker);
  });

  return results;
}
